import {
  NavigationAction,
  type NavigationState,
  PartialRoute,
  type PartialState,
  type NavigationContainerRef,
  ParamListBase,
} from '@react-navigation/native';
import { IS_DOM } from 'expo/dom';
import * as Linking from 'expo-linking';
import { type RefObject } from 'react';
import { Platform } from 'react-native';

import { store } from './router-store';
import {
  emitDomDismiss,
  emitDomDismissAll,
  emitDomGoBack,
  emitDomLinkEvent,
  emitDomSetParams,
} from '../domComponents/emitDomEvent';
import { ResultState } from '../fork/getStateFromPath';
import { applyRedirects } from '../getRoutesRedirects';
import { resolveHref, resolveHrefStringWithSegments } from '../link/href';
import { matchDynamicName } from '../matchers';
import { appendInternalExpoRouterParams, type InternalExpoRouterParams } from '../navigationParams';
import { Href } from '../types';
import { SingularOptions } from '../useScreens';
import { shouldLinkExternally } from '../utils/url';

function assertIsReady() {
  if (!store.navigationRef.isReady()) {
    throw new Error(
      'Attempted to navigate before mounting the Root Layout component. Ensure the Root Layout component is rendering a Slot, or other navigator on the first render.'
    );
  }
}

export const routingQueue = {
  queue: [] as NavigationAction[],
  subscribers: new Set<() => void>(),
  subscribe(callback: () => void) {
    routingQueue.subscribers.add(callback);
    return () => {
      routingQueue.subscribers.delete(callback);
    };
  },
  snapshot() {
    return routingQueue.queue;
  },
  add(action: NavigationAction) {
    routingQueue.queue.push(action);
    for (const callback of routingQueue.subscribers) {
      callback();
    }
  },
  run(ref: RefObject<NavigationContainerRef<ParamListBase> | null>) {
    // Reset the identity of the queue.
    const events = routingQueue.queue;
    routingQueue.queue = [];
    let action: NavigationAction | undefined;
    while ((action = events.shift())) {
      if (ref.current) {
        ref.current.dispatch(action);
      }
    }
  },
};

export type NavigationOptions = Omit<LinkToOptions, 'event'>;

export function navigate(url: Href, options?: NavigationOptions) {
  return linkTo(resolveHref(url), { ...options, event: 'NAVIGATE' });
}

export function reload() {
  // TODO(EvanBacon): add `reload` support.
  throw new Error('The reload method is not implemented in the client-side router yet.');
}

export function prefetch(href: Href, options?: NavigationOptions) {
  return linkTo(resolveHref(href), { ...options, event: 'PRELOAD' });
}

export function push(url: Href, options?: NavigationOptions) {
  return linkTo(resolveHref(url), { ...options, event: 'PUSH' });
}

export function dismiss(count: number = 1) {
  if (emitDomDismiss(count)) {
    return;
  }

  routingQueue.add({ type: 'POP', payload: { count } });
}

export function dismissTo(href: Href, options?: NavigationOptions) {
  return linkTo(resolveHref(href), { ...options, event: 'POP_TO' });
}

export function replace(url: Href, options?: NavigationOptions) {
  return linkTo(resolveHref(url), { ...options, event: 'REPLACE' });
}

export function dismissAll() {
  if (emitDomDismissAll()) {
    return;
  }
  routingQueue.add({ type: 'POP_TO_TOP' });
}

export function goBack() {
  if (emitDomGoBack()) {
    return;
  }
  assertIsReady();
  routingQueue.add({ type: 'GO_BACK' });
}

export function canGoBack(): boolean {
  if (IS_DOM) {
    throw new Error(
      'canGoBack imperative method is not supported. Pass the property to the DOM component instead.'
    );
  }
  // Return a default value here if the navigation hasn't mounted yet.
  // This can happen if the user calls `canGoBack` from the Root Layout route
  // before mounting a navigator. This behavior exists due to React Navigation being dynamically
  // constructed at runtime. We can get rid of this in the future if we use
  // the static configuration internally.
  if (!store.navigationRef.isReady()) {
    return false;
  }
  return store.navigationRef?.current?.canGoBack() ?? false;
}

export function canDismiss(): boolean {
  if (IS_DOM) {
    throw new Error(
      'canDismiss imperative method is not supported. Pass the property to the DOM component instead.'
    );
  }
  let state = store.state;

  // Keep traversing down the state tree until we find a stack navigator that we can pop
  while (state) {
    if (state.type === 'stack' && state.routes.length > 1) {
      return true;
    }
    if (state.index === undefined) return false;

    state = state.routes?.[state.index]?.state as any;
  }

  return false;
}

export function setParams(
  params: Record<string, undefined | string | number | (string | number)[]> = {}
) {
  if (emitDomSetParams(params)) {
    return;
  }
  assertIsReady();
  return (store.navigationRef?.current?.setParams as any)(params);
}

export type LinkToOptions = {
  event?: string;

  /**
   * Relative URL references are either relative to the directory or the document. By default, relative paths are relative to the document.
   * @see: [MDN's documentation on Resolving relative references to a URL](https://developer.mozilla.org/en-US/docs/Web/API/URL_API/Resolving_relative_references).
   */
  relativeToDirectory?: boolean;

  /**
   * Include the anchor when navigating to a new navigator
   */
  withAnchor?: boolean;

  /**
   * When navigating in a Stack, remove all screen from the history that match the singular condition
   *
   * If used with `push`, the history will be filtered even if no navigation occurs.
   */
  dangerouslySingular?: SingularOptions;

  __internal__PreviewKey?: string;
};

export function linkTo(originalHref: Href, options: LinkToOptions = {}) {
  originalHref = typeof originalHref == 'string' ? originalHref : resolveHref(originalHref);
  let href: string | undefined | null = originalHref;

  if (emitDomLinkEvent(href, options)) {
    return;
  }

  if (shouldLinkExternally(href)) {
    if (href.startsWith('//') && Platform.OS !== 'web') {
      href = `https:${href}`;
    }

    Linking.openURL(href);
    return;
  }

  assertIsReady();
  const navigationRef = store.navigationRef.current;

  if (navigationRef == null) {
    throw new Error(
      "Couldn't find a navigation object. Is your component inside NavigationContainer?"
    );
  }

  if (!store.linking) {
    throw new Error('Attempted to link to route when no routes are present');
  }

  if (href === '..' || href === '../') {
    navigationRef.goBack();
    return;
  }

  const rootState = navigationRef.getRootState();

  href = resolveHrefStringWithSegments(href, store.getRouteInfo(), options);
  href = applyRedirects(href, store.redirects);

  // If the href is undefined, it means that the redirect has already been handled the navigation
  if (!href) {
    return;
  }

  const state = store.linking.getStateFromPath!(href, store.linking.config);

  if (!state || state.routes.length === 0) {
    console.error('Could not generate a valid navigation state for the given path: ' + href);
    return;
  }

  routingQueue.add(
    getNavigateAction(
      state,
      rootState,
      options.event,
      options.withAnchor,
      options.dangerouslySingular,
      !!options.__internal__PreviewKey
    )
  );
}

function getNavigateAction(
  _actionState: ResultState,
  _navigationState: NavigationState,
  type = 'NAVIGATE',
  withAnchor?: boolean,
  singular?: SingularOptions,
  isPreviewNavigation?: boolean
) {
  /**
   * We need to find the deepest navigator where the action and current state diverge, If they do not diverge, the
   * lowest navigator is the target.
   *
   * By default React Navigation will target the current navigator, but this doesn't work for all actions
   * For example:
   *  - /deeply/nested/route -> /top-level-route the target needs to be the top-level navigator
   *  - /stack/nestedStack/page -> /stack1/nestedStack/other-page needs to target the nestedStack navigator
   *
   * This matching needs to done by comparing the route names and the dynamic path, for example
   * - /1/page -> /2/anotherPage needs to target the /[id] navigator
   *
   * Other parameters such as search params and hash are not evaluated.
   */

  const { actionStateRoute, navigationState } = findDivergentState(
    _actionState,
    _navigationState,
    type === 'PRELOAD'
  );

  /*
   * We found the target navigator, but the payload is in the incorrect format
   * We need to convert the action state to a payload that can be dispatched
   */
  const rootPayload = getPayloadFromStateRoute(actionStateRoute || {});

  if (type === 'PUSH' && navigationState.type !== 'stack') {
    type = 'NAVIGATE';
  } else if (navigationState.type === 'expo-tab') {
    type = 'JUMP_TO';
  } else if (type === 'REPLACE' && navigationState.type === 'drawer') {
    type = 'JUMP_TO';
  }

  if (withAnchor) {
    if (rootPayload.params.initial) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`The parameter 'initial' is a reserved parameter name in React Navigation`);
      }
    }
    /*
     * The logic for initial can seen backwards depending on your perspective
     *   True: The initialRouteName is not loaded. The incoming screen is the initial screen (default)
     *   False: The initialRouteName is loaded. THe incoming screen is placed after the initialRouteName
     *
     * withAnchor flips the perspective.
     *   True: You want the initialRouteName to load.
     *   False: You do not want the initialRouteName to load.
     */
    // Set initial on root and all nested params so anchors are loaded at every level
    let currentParams = rootPayload.params;
    while (currentParams) {
      currentParams.initial = !withAnchor;
      currentParams = currentParams.params;
    }
  }

  const expoParams: InternalExpoRouterParams = isPreviewNavigation
    ? {
        __internal__expo_router_is_preview_navigation: true,
        __internal_expo_router_no_animation: true,
      }
    : {};
  const params = appendInternalExpoRouterParams(rootPayload.params, expoParams);

  return {
    type,
    target: navigationState.key,
    payload: {
      // key: rootPayload.key,
      name: rootPayload.screen,
      params,
      singular,
    },
  };
}

/**
 * React Navigation uses params to store information about the screens, rather then create new state for each level.
 * This function traverses the action state that will not be part of state and returns a payload that can be used in action.
 */
export function getPayloadFromStateRoute(_actionStateRoute: PartialRoute<any>) {
  const rootPayload: Record<string, any> = { params: {} };
  let payload = rootPayload;
  let params = payload.params;
  let actionStateRoute: PartialRoute<any> | undefined = _actionStateRoute;

  while (actionStateRoute) {
    Object.assign(params, { ...payload.params, ...actionStateRoute.params });
    // Assign the screen name to the payload
    payload.screen = actionStateRoute.name;
    // Merge the params, ensuring that we create a new object
    payload.params = { ...params };

    // Params don't include the screen, thats a separate attribute
    delete payload.params['screen'];

    // Continue down the payload tree
    // Initially these values are separate, but React Nav merges them after the first layer
    payload = payload.params;
    params = payload;

    actionStateRoute = actionStateRoute.state?.routes[actionStateRoute.state?.routes.length - 1];
  }
  return rootPayload;
}

/*
 * Traverse the state tree comparing the current state and the action state until we find where they diverge
 */
export function findDivergentState(
  _actionState: ResultState,
  _navigationState: NavigationState,
  // If true, look through all tabs to find the target state, rather then just the current tab
  lookThroughAllTabs: boolean = false
) {
  let actionState: PartialState<NavigationState> | undefined = _actionState;
  let navigationState: NavigationState | undefined = _navigationState;
  let actionStateRoute: PartialRoute<any> | undefined;
  const navigationRoutes = [];
  while (actionState && navigationState) {
    actionStateRoute = actionState.routes[actionState.routes.length - 1];
    const stateRoute = (() => {
      if (navigationState.type === 'tab' && lookThroughAllTabs) {
        return (
          navigationState.routes.find((route) => route.name === actionStateRoute?.name) ||
          navigationState.routes[navigationState.index ?? 0]
        );
      }
      return navigationState.routes[navigationState.index ?? 0];
    })();

    const childState: PartialState<NavigationState> | undefined = actionStateRoute.state;
    const nextNavigationState = stateRoute.state;

    const dynamicName = matchDynamicName(actionStateRoute.name);

    const didActionAndCurrentStateDiverge =
      actionStateRoute.name !== stateRoute.name ||
      !childState ||
      !nextNavigationState ||
      (dynamicName &&
        // @ts-expect-error: TODO(@kitten): This isn't properly typed, so the index access fails
        actionStateRoute.params?.[dynamicName.name] !== stateRoute.params?.[dynamicName.name]);

    if (didActionAndCurrentStateDiverge) {
      // If we are looking through all tabs, we need to add new tab id if this is the last route
      // Otherwise we wouldn't be able to change the tab
      if (navigationState.type === 'tab' && lookThroughAllTabs) {
        navigationRoutes.push(stateRoute);
      }
      break;
    }

    navigationRoutes.push(stateRoute);

    actionState = childState;
    navigationState = nextNavigationState as NavigationState;
  }

  return {
    actionState,
    navigationState,
    actionStateRoute,
    navigationRoutes,
  };
}
